import { ROUTES } from "@template/types";

export interface FinanceNavItem {
  id: string;
  label: string;
  perm: string;
  route: string;
  /** Icon key resolved to a MUI icon component in AppLayout. */
  icon: string;
}

export interface FinanceNavGroup {
  group: string;
  items: FinanceNavItem[];
}

/**
 * Mirrors the source prototype's `NAV0` — grouped nav sections, each item
 * gated by a permission key. Ported 1:1 (same ids, same groups, same perms).
 */
export const FINANCE_NAV: FinanceNavGroup[] = [
  {
    group: "Planning",
    items: [
      {
        id: "dashboard",
        label: "Dashboard",
        perm: "dashboard.view",
        route: ROUTES.financeDashboard,
        icon: "dashboard",
      },
      {
        id: "summary",
        label: "Annual Budget Summary",
        perm: "reports.view",
        route: ROUTES.financeAnnualSummary,
        icon: "summary",
      },
      {
        id: "budget",
        label: "Budget Planning",
        perm: "budget.view",
        route: ROUTES.financeBudgets,
        icon: "budget",
      },
      {
        id: "actuals",
        label: "Actual Expenses",
        perm: "budget.view",
        route: ROUTES.financeActuals,
        icon: "actuals",
      },
      {
        id: "bva",
        label: "Budget vs Actual",
        perm: "budget.view",
        route: ROUTES.financeBudgetVsActual,
        icon: "bva",
      },
      {
        id: "monthly",
        label: "Monthly Analysis",
        perm: "budget.view",
        route: ROUTES.financeMonthlyAnalysis,
        icon: "monthly",
      },
      {
        id: "forecast",
        label: "Forecast & Scenarios",
        perm: "budget.view",
        route: ROUTES.financeForecast,
        icon: "forecast",
      },
    ],
  },
  {
    group: "Financials",
    items: [
      {
        id: "revenue",
        label: "Revenue Management",
        perm: "revenue.view",
        route: ROUTES.financeRevenue,
        icon: "revenue",
      },
      {
        id: "cashflow",
        label: "Cash Flow",
        perm: "reports.view",
        route: ROUTES.financeCashFlow,
        icon: "cashflow",
      },
      {
        id: "pnl",
        label: "Profit & Loss",
        perm: "reports.view",
        route: ROUTES.financePnl,
        icon: "pnl",
      },
      {
        id: "dept",
        label: "Department Budget",
        perm: "budget.view",
        route: ROUTES.financeDepartmentBudget,
        icon: "dept",
      },
      {
        id: "cc",
        label: "Cost Centre Budget",
        perm: "budget.view",
        route: ROUTES.financeCostCentreBudget,
        icon: "cc",
      },
    ],
  },
  {
    group: "Governance",
    items: [
      {
        id: "upload",
        label: "Tally Import & Versions",
        perm: "tally.upload",
        route: ROUTES.financeTallyImport,
        icon: "upload",
      },
      {
        id: "approvals",
        label: "Approvals",
        perm: "approval.act",
        route: ROUTES.financeApprovals,
        icon: "approvals",
      },
      {
        id: "locking",
        label: "Period Locking",
        perm: "freeze.month",
        route: ROUTES.financePeriodControl,
        icon: "locking",
      },
      {
        id: "compare",
        label: "Version Compare",
        perm: "version.view",
        route: ROUTES.financeVersions,
        icon: "compare",
      },
      {
        id: "audit",
        label: "Audit Trail",
        perm: "audit.view",
        route: ROUTES.financeAuditTrail,
        icon: "audit",
      },
    ],
  },
  {
    group: "Administration",
    items: [
      {
        id: "users",
        label: "Users & Access",
        perm: "users.view",
        route: ROUTES.financeUsers,
        icon: "users",
      },
      {
        id: "roles",
        label: "Roles & Permissions",
        perm: "users.view",
        route: ROUTES.financeRoles,
        icon: "roles",
      },
      {
        id: "reports",
        label: "Reports & Exports",
        perm: "reports.view",
        route: ROUTES.financeReports,
        icon: "reports",
      },
      {
        id: "settings",
        label: "System Settings",
        perm: "settings.manage",
        route: ROUTES.financeSettings,
        icon: "settings",
      },
    ],
  },
];

export const FINANCE_NAV_ROUTE_BY_ID: Record<string, string> = FINANCE_NAV.flatMap(
  (g) => g.items,
).reduce(
  (acc, item) => {
    acc[item.id] = item.route;
    return acc;
  },
  {} as Record<string, string>,
);
